/* eslint-disable no-use-before-define */
import { createApi } from '@reduxjs/toolkit/query/react';
import { createEventAPI } from 'requests/event';
import { getBackgroundJobList } from 'store/slices/jobSlice';
import { registrationApi } from './registrationApi';
import rcBaseQuery from './baseQuery';

export const titleApi = createApi({
    reducerPath: 'titleApi',
    baseQuery: rcBaseQuery,
    tagTypes: ['Title', 'TitleList', 'InfringementList'],
    endpoints: (builder) => ({
        // ========================
        // QUERIES
        // ========================

        // Scoped title list (project or registration)
        getTitles: builder.query({
            query: ({ projectID, regID } = {}) => ({
                url: 'api/v2/titles',
                params: { projectID, regID }
            }),
            providesTags: (result, error, arg) => [{ type: 'TitleList', id: arg?.projectID || 'ALL' }]
        }),

        // Full single title (with infringements/actions)
        getTitle: builder.query({
            query: ({ id }) => ({
                url: `api/v2/titles/${id}`
            }),
            providesTags: (result, error, { id }) => [{ type: 'Title', id }]
        }),

        // All infringements grouped by title
        getInfringements: builder.query({
            query: () => ({
                url: 'api/v2/infringements'
            }),
            providesTags: [{ type: 'InfringementList', id: 'ALL' }]
        }),

        // All titles summary (lightweight, non-paginated — server handles DynamoDB pagination)
        getTitlesSummary: builder.query({
            query: () => ({
                url: 'api/v2/titles/summary'
            }),
            providesTags: [{ type: 'TitleList', id: 'ALL' }]
        }),

        // ========================
        // MUTATIONS — Create/Delete (invalidate list)
        // ========================

        createTitle: builder.mutation({
            query: ({ projectID, data }) => ({
                url: 'api/v2/titles',
                method: 'POST',
                body: { projectID, ...data }
            }),
            invalidatesTags: (result, error, { projectID }) => [
                { type: 'TitleList', id: projectID },
                { type: 'TitleList', id: 'ALL' }
            ]
        }),

        deleteTitle: builder.mutation({
            query: ({ id }) => ({
                url: `api/v2/titles/${id}`,
                method: 'DELETE'
            }),
            invalidatesTags: ['TitleList']
        }),

        bulkCreateTitles: builder.mutation({
            query: ({ projectID, data }) => ({
                url: 'api/v2/titles/bulk',
                method: 'POST',
                body: { projectID, titles: data }
            }),
            invalidatesTags: (result, error, { projectID }) => [
                { type: 'TitleList', id: projectID },
                { type: 'TitleList', id: 'ALL' }
            ]
        }),

        bulkUpdateTitles: builder.mutation({
            query: ({ titles, data, projectID }) => ({
                url: 'api/v2/titles/bulk',
                method: 'PUT',
                body: { titles, data, projectID }
            }),
            invalidatesTags: (result, error, { projectID }) => {
                const tags = [{ type: 'TitleList', id: 'ALL' }];
                if (projectID) tags.push({ type: 'TitleList', id: projectID });
                return tags;
            },
            async onQueryStarted({ data }, { dispatch, queryFulfilled }) {
                try {
                    const { data: result } = await queryFulfilled;
                    // When the work is queued as a background job, the API returns
                    // immediately. Refetch the job list so polling picks up the new
                    // job and the title list refreshes when it completes.
                    if (result?.isOnline === false) {
                        dispatch(getBackgroundJobList());
                    }
                    // Bulk-setting Registered/regNo also writes a registration
                    // record server-side — drop the registration caches so the
                    // All Registrations list reflects it.
                    if (data?.isReg === 'yes' && data?.regNo) {
                        dispatch(registrationApi.util.invalidateTags([{ type: 'RegistrationList', id: 'ALL' }]));
                    }
                } catch {
                    /* handled by RTK Query error state */
                }
            }
        }),

        bulkDeleteTitles: builder.mutation({
            query: ({ titles }) => ({
                url: 'api/v2/titles/bulk/delete',
                method: 'POST',
                body: { titles }
            }),
            invalidatesTags: [{ type: 'TitleList', id: 'ALL' }]
        }),

        // ========================
        // MUTATIONS — Update (patch cache, no refetch)
        // ========================

        updateTitle: builder.mutation({
            query: ({ id, data }) => ({
                url: `api/v2/titles/${id}`,
                method: 'PUT',
                body: data
            }),
            async onQueryStarted({ id }, { dispatch, queryFulfilled }) {
                try {
                    const { data: updatedTitle } = await queryFulfilled;
                    patchCaches(dispatch, id, updatedTitle);
                    // When the user marks a title as Registered=yes with a regNo,
                    // the backend also flips the linked registration record to
                    // Success — refresh the registration caches so the All
                    // Registrations list reflects it without a manual reload.
                    if (updatedTitle?.isReg === 'yes' && updatedTitle?.regNo && updatedTitle?.regID) {
                        dispatch(
                            registrationApi.util.invalidateTags([
                                { type: 'RegistrationList', id: 'ALL' },
                                { type: 'Registration', id: updatedTitle.regID }
                            ])
                        );
                    }
                } catch {
                    /* handled by RTK Query error state */
                }
            }
        }),

        addInfringement: builder.mutation({
            query: ({ titleID, data }) => ({
                url: `api/v2/titles/${titleID}/infringement`,
                method: 'POST',
                body: data
            }),
            invalidatesTags: [{ type: 'InfringementList', id: 'ALL' }],
            async onQueryStarted({ titleID }, { dispatch, queryFulfilled }) {
                try {
                    const { data: updatedTitle } = await queryFulfilled;
                    patchCaches(dispatch, titleID, updatedTitle);
                } catch {
                    /* handled by RTK Query error state */
                }
            }
        }),

        updateInfringement: builder.mutation({
            query: ({ titleID, idx, data }) => ({
                url: `api/v2/titles/${titleID}/infringement`,
                method: 'PUT',
                params: { idx },
                body: data
            }),
            invalidatesTags: [{ type: 'InfringementList', id: 'ALL' }],
            async onQueryStarted({ titleID }, { dispatch, queryFulfilled }) {
                try {
                    const { data: updatedTitle } = await queryFulfilled;
                    patchCaches(dispatch, titleID, updatedTitle);
                } catch {
                    /* handled by RTK Query error state */
                }
            }
        }),

        addAction: builder.mutation({
            query: ({ titleID, infIdx, data }) => ({
                url: `api/v2/titles/${titleID}/action`,
                method: 'POST',
                params: { inf: infIdx },
                body: data
            }),
            async onQueryStarted({ titleID }, { dispatch, queryFulfilled }) {
                try {
                    const { data: updatedTitle } = await queryFulfilled;
                    patchCaches(dispatch, titleID, updatedTitle);
                } catch {
                    /* handled by RTK Query error state */
                }
            }
        }),

        updateAction: builder.mutation({
            query: ({ titleID, actionID, infIdx, data }) => ({
                url: `api/v2/titles/${titleID}/action/${actionID}`,
                method: 'PUT',
                params: { inf: infIdx },
                body: data
            }),
            async onQueryStarted({ titleID }, { dispatch, queryFulfilled }) {
                try {
                    const { data: updatedTitle } = await queryFulfilled;
                    patchCaches(dispatch, titleID, updatedTitle);
                } catch {
                    /* handled by RTK Query error state */
                }
            }
        }),

        addActionNote: builder.mutation({
            query: ({ titleID, actionID, infIdx, data }) => ({
                url: `api/v2/titles/${titleID}/action/${actionID}/note`,
                method: 'POST',
                params: { inf: infIdx },
                body: data
            }),
            async onQueryStarted({ titleID }, { dispatch, queryFulfilled }) {
                try {
                    const { data: updatedTitle } = await queryFulfilled;
                    patchCaches(dispatch, titleID, updatedTitle);
                } catch {
                    /* handled by RTK Query error state */
                }
            }
        }),

        // ========================
        // EMAIL MUTATIONS — patch cache + create event
        // ========================

        sendCndEmail: builder.mutation({
            query: ({ titleID, actionID, infIdx }) => ({
                url: `api/v2/titles/${titleID}/action/${actionID}/email/cnd`,
                method: 'POST',
                params: { inf: infIdx }
            }),
            async onQueryStarted({ titleID, event }, { dispatch, queryFulfilled }) {
                try {
                    const { data: updatedTitle } = await queryFulfilled;
                    patchCaches(dispatch, titleID, updatedTitle);
                    if (event) {
                        const eventResponse = await createEventAPI({ data: event });
                        dispatch({ type: 'event/create/fulfilled', payload: eventResponse });
                    }
                } catch {
                    /* handled by RTK Query error state */
                }
            }
        }),

        sendCnd2Email: builder.mutation({
            query: ({ titleID, actionID, infIdx }) => ({
                url: `api/v2/titles/${titleID}/action/${actionID}/email/cnd2`,
                method: 'POST',
                params: { inf: infIdx }
            }),
            async onQueryStarted({ titleID, event }, { dispatch, queryFulfilled }) {
                try {
                    const { data: updatedTitle } = await queryFulfilled;
                    patchCaches(dispatch, titleID, updatedTitle);
                    if (event) {
                        const eventResponse = await createEventAPI({ data: event });
                        dispatch({ type: 'event/create/fulfilled', payload: eventResponse });
                    }
                } catch {
                    /* handled by RTK Query error state */
                }
            }
        }),

        sendDmcaEmail: builder.mutation({
            query: ({ titleID, actionID, infIdx }) => ({
                url: `api/v2/titles/${titleID}/action/${actionID}/email/dmca`,
                method: 'POST',
                params: { inf: infIdx }
            }),
            async onQueryStarted({ titleID }, { dispatch, queryFulfilled }) {
                try {
                    const { data: updatedTitle } = await queryFulfilled;
                    patchCaches(dispatch, titleID, updatedTitle);
                } catch {
                    /* handled by RTK Query error state */
                }
            }
        }),

        sendAttorneyEmail: builder.mutation({
            query: ({ titleID, actionID, infIdx }) => ({
                url: `api/v2/titles/${titleID}/action/${actionID}/email/attorney`,
                method: 'POST',
                params: { inf: infIdx }
            }),
            async onQueryStarted({ titleID, event }, { dispatch, queryFulfilled }) {
                try {
                    const { data: updatedTitle } = await queryFulfilled;
                    patchCaches(dispatch, titleID, updatedTitle);
                    if (event) {
                        const eventResponse = await createEventAPI({ data: event });
                        dispatch({ type: 'event/create/fulfilled', payload: eventResponse });
                    }
                } catch {
                    /* handled by RTK Query error state */
                }
            }
        }),

        sendAttorney2Email: builder.mutation({
            query: ({ titleID, actionID, infIdx }) => ({
                url: `api/v2/titles/${titleID}/action/${actionID}/email/attorney2`,
                method: 'POST',
                params: { inf: infIdx }
            }),
            async onQueryStarted({ titleID, event }, { dispatch, queryFulfilled }) {
                try {
                    const { data: updatedTitle } = await queryFulfilled;
                    patchCaches(dispatch, titleID, updatedTitle);
                    if (event) {
                        const eventResponse = await createEventAPI({ data: event });
                        dispatch({ type: 'event/create/fulfilled', payload: eventResponse });
                    }
                } catch {
                    /* handled by RTK Query error state */
                }
            }
        })
    })
});

// Helper: patch getTitle, getTitlesSummary, and getTitles (project-scoped) caches after a mutation.
// Defined after titleApi so there's no forward reference.
function patchCaches(dispatch, id, updatedTitle) {
    dispatch(
        titleApi.util.updateQueryData('getTitle', { id }, (draft) => {
            Object.assign(draft, updatedTitle);
        })
    );
    // Patch summary list cache
    dispatch(
        titleApi.util.updateQueryData('getTitlesSummary', undefined, (draft) => {
            if (draft?.titles) {
                const idx = draft.titles.findIndex((t) => t.id === id);
                if (idx !== -1) Object.assign(draft.titles[idx], updatedTitle);
            }
        })
    );
    // Patch project-scoped list cache (no-op if cache entry doesn't exist)
    if (updatedTitle?.projectID) {
        dispatch(
            titleApi.util.updateQueryData('getTitles', { projectID: updatedTitle.projectID }, (draft) => {
                if (draft?.titles) {
                    const idx = draft.titles.findIndex((t) => t.id === id);
                    if (idx !== -1) Object.assign(draft.titles[idx], updatedTitle);
                }
            })
        );
    }
}

/**
 * Convenience wrapper — fetches all titles via the lightweight summary endpoint.
 * Server handles DynamoDB pagination internally, so this is a single request.
 */
export function useGetAllTitlesQuery(options = {}) {
    return useGetTitlesSummaryQuery(undefined, options);
}

export const {
    useGetTitlesQuery,
    useGetTitleQuery,
    useGetInfringementsQuery,
    useGetTitlesSummaryQuery,
    useCreateTitleMutation,
    useUpdateTitleMutation,
    useDeleteTitleMutation,
    useBulkCreateTitlesMutation,
    useBulkUpdateTitlesMutation,
    useBulkDeleteTitlesMutation,
    useAddInfringementMutation,
    useUpdateInfringementMutation,
    useAddActionMutation,
    useUpdateActionMutation,
    useAddActionNoteMutation,
    useSendCndEmailMutation,
    useSendCnd2EmailMutation,
    useSendDmcaEmailMutation,
    useSendAttorneyEmailMutation,
    useSendAttorney2EmailMutation
} = titleApi;
